/**
 * Financial Charts - Business Value Calculator
 * Sprint 7: Day 3
 *
 * Two Chart.js visualizations:
 * 1. Cost vs Benefit Line Chart: Monthly costs and benefits over 36 months
 * 2. Cumulative Cash Flow Chart: Running total with break-even marker
 *
 * Features:
 * - Professional gradients and colors
 * - Rich tooltips with formatted values
 * - Responsive sizing
 * - Break-even markers
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { CashFlowRow } from './CashFlowTable';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface FinancialChartsProps {
  cashFlowData: CashFlowRow[];
  breakEvenMonth: number | null;
}

export const FinancialCharts: React.FC<FinancialChartsProps> = ({
  cashFlowData,
  breakEvenMonth
}) => {
  // Prepare data for Cost vs Benefit chart
  const labels = cashFlowData.map(row => `M${row.month}`);
  const costsData = cashFlowData.map(row => row.costs);
  const benefitsData = cashFlowData.map(row => row.benefits);
  const cumulativeData = cashFlowData.map(row => row.cumulativeCashFlow);

  // Chart 1: Cost vs Benefit Line Chart
  const costBenefitData = {
    labels,
    datasets: [
      {
        label: 'Monthly Costs',
        data: costsData,
        borderColor: 'rgba(252, 129, 129, 1)',
        backgroundColor: 'rgba(252, 129, 129, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5
      },
      {
        label: 'Monthly Benefits',
        data: benefitsData,
        borderColor: 'rgba(72, 187, 120, 1)',
        backgroundColor: 'rgba(72, 187, 120, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5
      }
    ]
  };

  const costBenefitOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      title: {
        display: true,
        text: 'Monthly Costs vs Benefits (36 Months)',
        font: {
          size: 14,
          weight: 700
        },
        color: '#2d3748'
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 600
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context: { dataset: { label?: string }; parsed: { y: number | null } }) {
            const label = context.dataset.label || '';
            const value = context.parsed.y ?? 0;
            return `${label}: $${value.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: string | number) {
            return '$' + (Number(value) / 1000).toFixed(0) + 'k';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxTicksLimit: 12,
          font: {
            size: 10
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  // Chart 2: Cumulative Cash Flow Chart
  const cumulativeData_chart = {
    labels,
    datasets: [
      {
        label: 'Cumulative Cash Flow',
        data: cumulativeData,
        borderColor: 'rgba(0, 148, 171, 1)',
        backgroundColor: function(context: { chart: { ctx: CanvasRenderingContext2D } }) {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, 'rgba(0, 148, 171, 0.3)');
          gradient.addColorStop(1, 'rgba(0, 148, 171, 0.05)');
          return gradient;
        },
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 6
      },
      // Zero line for reference
      {
        label: 'Break-Even Line',
        data: Array(36).fill(0),
        borderColor: 'rgba(0, 0, 0, 0.2)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0
      }
    ]
  };

  const cumulativeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 600
          },
          filter: function(item: { text: string }) {
            // Hide zero line from legend
            return item.text !== 'Break-Even Line';
          }
        }
      },
      title: {
        display: true,
        text: 'Cumulative Cash Flow Over Time',
        font: {
          size: 14,
          weight: 700
        },
        color: '#2d3748'
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 600
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context: { datasetIndex: number; parsed: { y: number | null } }) {
            if (context.datasetIndex === 1) return ''; // Skip zero line
            const value = context.parsed.y ?? 0;
            const prefix = value >= 0 ? '$' : '-$';
            return `Cumulative: ${prefix}${Math.abs(value).toLocaleString()}`;
          },
          footer: function(contexts: Array<{ dataIndex: number }>) {
            if (breakEvenMonth && contexts[0].dataIndex + 1 === breakEvenMonth) {
              return '🎉 Break-Even Month!';
            }
            return '';
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: function(value: string | number) {
            const val = Number(value);
            const prefix = val >= 0 ? '$' : '-$';
            return `${prefix}${Math.abs(val / 1000).toFixed(0)}k`;
          }
        },
        grid: {
          color: function(context: { tick: { value: number } }) {
            // Highlight zero line
            if (context.tick.value === 0) {
              return 'rgba(0, 0, 0, 0.2)';
            }
            return 'rgba(0, 0, 0, 0.05)';
          },
          lineWidth: function(context: { tick: { value: number } }) {
            if (context.tick.value === 0) {
              return 2;
            }
            return 1;
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxTicksLimit: 12,
          font: {
            size: 10
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  return (
    <div className="financial-charts">
      <div className="chart-container">
        <Line data={costBenefitData} options={costBenefitOptions} />
      </div>

      <div className="chart-container">
        <Line data={cumulativeData_chart} options={cumulativeOptions} />
        {breakEvenMonth && (
          <div className="break-even-marker">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            Break-even achieved at Month {breakEvenMonth}
          </div>
        )}
      </div>

      <style>{`
        .financial-charts {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .chart-container {
          position: relative;
          height: 400px;
          padding: 2rem;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
        }

        .break-even-marker {
          position: absolute;
          top: 1rem;
          right: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-weight: 700;
          color: #48bb78;
          background: rgba(72, 187, 120, 0.1);
          border: 2px solid #48bb78;
          border-radius: 8px;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .chart-container {
            height: 300px;
            padding: 1.5rem;
          }
        }

        @media (max-width: 640px) {
          .chart-container {
            height: 250px;
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FinancialCharts;
